import { isIndexedDBSupported } from '@pulse/common';
import { BehaviorSubject, catchError, filter, firstValueFrom, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { sharedDefaultConfigs } from '../../configuration';
import { PulseEnvironment } from '../../globals';

export class IndexedDBService {
    private _db?: IDBDatabase;
    private connectionState$ = new BehaviorSubject<'NOT_CONNECTED' | 'CONNECTING' | 'CONNECTED'>(
        'NOT_CONNECTED',
    );
    private storeList = ['settingsStore', 'attributesStore'];
    private pulseDB = 'pulseDB';

    get db(): IDBDatabase {
        if (!this._db) {
            throw Error('IndexDB store not exist ');
        }

        return this._db;
    }

    constructor(private env: PulseEnvironment) {}

    async truncate(tableName: string) {
        if (!isIndexedDBSupported(this.env.win)) {
            return;
        }
        const transaction = await this.getTransaction(tableName, 'readwrite');

        return new Promise<void>((resolve, reject) => {
            try {
                const store = transaction.objectStore(tableName);
                const request = store.clear();
                request.onerror = (err: any) => {
                    this.logerr(err);
                    reject(err);
                };
                request.onsuccess = () => {
                    resolve(request.result);
                };
            } catch (err) {
                reject(err);
            }
        });
    }

    async getValue(tableName: string, name: string): Promise<any> {
        if (!isIndexedDBSupported(this.env.win)) {
            return;
        }
        const transaction = await this.getTransaction(tableName, 'readonly');

        return new Promise((resolve, reject) => {
            try {
                const store = transaction.objectStore(tableName);
                const request = store.get(name);
                request.onerror = this.logerr;
                request.onsuccess = () => {
                    resolve(request.result);
                };
            } catch (err) {
                reject(err);
            }
        });
    }

    async getAllValue<T>(tableName: string): Promise<T | void> {
        if (!isIndexedDBSupported(this.env.win)) {
            return Promise.resolve();
        }
        const transaction = await this.getTransaction(tableName, 'readonly');

        return new Promise((resolve, reject) => {
            try {
                const store = transaction.objectStore(tableName);
                const request = store.getAll();
                request.onerror = this.logerr;
                request.onsuccess = () => {
                    resolve(request.result as T);
                };
            } catch (err) {
                reject(err);
            }
        });
    }

    async putValue(tableName: string, value: object): Promise<boolean> {
        if (!isIndexedDBSupported(this.env.win)) {
            return Promise.resolve(false);
        }
        const transaction = await this.getTransaction(tableName, 'readwrite');

        return new Promise((resolve, reject) => {
            try {
                const store = transaction.objectStore(tableName);
                const request = store.put(value);

                request.onsuccess = () => {
                    resolve(true);
                };
                request.onerror = this.logerr;
            } catch (err) {
                reject(err);
            }
        });
    }

    async putBulkValue(tableName: string, values: object[]) {
        if (!isIndexedDBSupported(this.env.win)) {
            return;
        }
        const transaction = await this.getTransaction(tableName, 'readwrite');
        const store = transaction.objectStore(tableName);
        for (const value of values) {
            store.put(value);
        }

        return await this.getAllValue(tableName);
    }

    async deleteValue(tableName: string, id: string) {
        if (!isIndexedDBSupported(this.env.win)) {
            return;
        }
        const transaction = await this.getTransaction(tableName, 'readwrite');

        return new Promise((resolve, reject) => {
            try {
                const store = transaction.objectStore(tableName);
                const request = store.get(id);

                request.onsuccess = () => {
                    if (!request.result) {
                        resolve('-1');
                    }
                    store.delete(id).onsuccess = () => resolve(id);
                };
                request.onerror = this.logerr;
            } catch (err) {
                reject(err);
            }
        });
    }

    async connectDB() {
        if (!isIndexedDBSupported(this.env.win)) {
            return;
        }
        this.connectionState$.next('CONNECTING');
        const request: IDBOpenDBRequest = this.env.win.indexedDB.open(this.pulseDB, 1);

        return new Promise((resolve, reject) => {
            try {
                request.onerror = (err: any) => {
                    this.connectionState$.next('NOT_CONNECTED');
                    this.logerr(err);
                };
                request.onsuccess = () => {
                    this.createObjectStore(request.result);
                    resolve(request.result);
                };

                request.onupgradeneeded = (e: any) => {
                    if (e.target && e.target?.result) {
                        this.createObjectStore(e.target?.result);
                    }
                };
            } catch (err) {
                this.connectionState$.next('NOT_CONNECTED');
                reject(err);
            }
        });
    }

    private logerr(err: any) {
        console.warn(err);
    }

    private createObjectStore(result: IDBDatabase) {
        this.storeList.forEach((store) => {
            if (!result.objectStoreNames.contains(store)) {
                const objStore = result.createObjectStore(store, {
                    autoIncrement: false,
                    keyPath: 'id',
                });
                objStore.createIndex('id', 'id', { unique: true });
                objStore.createIndex('time', 'time', { unique: false });
                objStore.createIndex('id_time', ['id', 'time'], { unique: true });
            }
        });

        this._db = result;
        this.connectionState$.next('CONNECTED');
        this.db.onclose = this.reconnectDB.bind(this);
        this.db.onabort = this.reconnectDB.bind(this);
    }

    private getTransaction(
        storeNames: string | string[],
        mode?: IDBTransactionMode,
        options?: IDBTransactionOptions,
    ): Promise<IDBTransaction> {
        if (this.connectionState$.getValue() === 'CONNECTED') {
            try {
                return Promise.resolve(this.db.transaction(storeNames, mode, options));
            } catch (err) {
                console.warn(err);
                this.connectDB();
            }
        }

        return firstValueFrom(
            this.connectionState$.pipe(
                filter((state) => state === 'CONNECTED'),
                map(() => this.db.transaction(storeNames, mode, options)),
                catchError((err) => {
                    this.logerr(err);

                    return throwError(() => err);
                }),
            ),
        );
    }

    private reconnectDB(err?: any): void {
        if (err) {
            this.logerr(`${sharedDefaultConfigs.handledErrorPrefix} ${JSON.stringify(err)}`);
        }
        this.connectDB();
    }
}
